/**
 * Themes, and the groups they are shown in.
 *
 * The list outgrew a single row of buttons, so it is grouped. The groups are
 * about mood rather than colour theory — "which of these do I want the room to
 * look like tonight" is the only question anyone is actually asking — and the
 * picker collapses them so the eight originals are not buried under the rest.
 *
 * Light is the only bright one. That is not a house style so much as an
 * observation: this gets used in a dim room on a projector, and a bright theme
 * is a decision, not a default.
 */

export const THEME_GROUPS = [
  {
    id: 'basic',
    label: 'Basics',
    hint: 'The two that came first.',
    themes: [
      { id: 'dark', label: 'Dark', swatch: '#2b2f3e' },
      { id: 'light', label: 'Light', swatch: '#f4f4f6' },
    ],
  },
  {
    id: 'warm',
    label: 'Warm and natural',
    hint: 'Firelight, moss, and the last half hour of the day.',
    themes: [
      { id: 'ember', label: 'Ember', swatch: '#3a2418' },
      { id: 'forest', label: 'Forest', swatch: '#1b3129' },
      { id: 'sunset', label: 'Sunset', swatch: '#ff4f73' },
      { id: 'aurora', label: 'Aurora', swatch: '#39e6a8' },
    ],
  },
  {
    id: 'neon',
    label: 'Neon and loud',
    hint: 'Bright, saturated, and not remotely subtle.',
    themes: [
      { id: 'ultraviolet', label: 'Ultraviolet', swatch: '#711cff' },
      { id: 'laser', label: 'Laser pop', swatch: '#00e5c3' },
      { id: 'vapor', label: 'Vapor', swatch: '#ff5fd2' },
      { id: 'arcade', label: 'Arcade', swatch: '#ffb300' },
    ],
  },
  {
    id: 'after-dark',
    label: 'After dark',
    hint: 'For the late half of the night.',
    themes: [
      { id: 'terminal', label: 'Terminal', swatch: '#27ff86' },
      { id: 'bloodmoon', label: 'Blood moon', swatch: '#8b0f1d' },
      { id: 'abyss', label: 'Abyss', swatch: '#0a3a4a' },
      { id: 'noir', label: 'Noir gold', swatch: '#c8a24a' },
    ],
  },
  {
    id: 'paper',
    label: 'Paper',
    hint: 'Ink on a page, printed at night.',
    themes: [{ id: 'mono', label: 'Newsprint', swatch: '#2a2722' }],
  },
] as const;

export type ThemeId = (typeof THEME_GROUPS)[number]['themes'][number]['id'];

export interface ThemeOption {
  id: ThemeId;
  label: string;
  swatch: string;
}

/** Every theme, flattened, for the places that just need the whole list. */
export const THEMES: readonly ThemeOption[] = THEME_GROUPS.flatMap((group) => [...group.themes]);

const STORAGE_KEY = 'src.theme';
const DEFAULT_THEME: ThemeId = 'dark';

export function isThemeId(value: string): value is ThemeId {
  return THEMES.some((t) => t.id === value);
}

/** Which group a theme belongs to, so the picker can open the right one. */
export function groupIdForTheme(theme: string): string {
  const group = THEME_GROUPS.find((candidate) =>
    candidate.themes.some((option) => option.id === theme),
  );
  return group?.id ?? THEME_GROUPS[0].id;
}

/**
 * The theme is mirrored into localStorage as well as the member's profile.
 *
 * The profile copy is the durable one and follows them across devices; the
 * local copy exists so the first paint after a reload is already correct
 * rather than flashing the default while /api/me is in flight.
 */
export function readStoredTheme(): ThemeId {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored && isThemeId(stored)) return stored;
  } catch {
    // Private browsing and blocked site data both throw here. A missing
    // preference is not worth breaking the page over.
  }
  return DEFAULT_THEME;
}

export function applyTheme(theme: string): void {
  const resolved = isThemeId(theme) ? theme : DEFAULT_THEME;
  document.documentElement.dataset['theme'] = resolved;
  try {
    localStorage.setItem(STORAGE_KEY, resolved);
  } catch {
    // Non-fatal: the theme still applies for this page view.
  }
}
